import type { SupabaseClient } from '@supabase/supabase-js';

/**
 * Upload a vault logo image to the 'vault-logos' storage bucket and return its public URL.
 * - Stores at bucket root using a timestamped random filename.
 * - Returns the public URL or throws on error.
 */
export async function uploadVaultLogoCore(supabase: SupabaseClient, file: File): Promise<string> {
    if (!file) throw new Error('file required');
    const ext = (file.name.split('.').pop() || 'jpg').toLowerCase();
    const fileName = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const { error } = await supabase.storage.from('vault-logos').upload(fileName, file, { upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('vault-logos').getPublicUrl(fileName);
    return data.publicUrl as string;
}

/**
 * Upload a generic image Blob/File to the 'images' bucket under the given folder path and return the public URL.
 * The folder should not start with a slash.
 */
export async function uploadImageToBucketCore(
    supabase: SupabaseClient,
    fileOrBlob: File | Blob,
    folder: string,
    contentType = 'image/jpeg'
): Promise<string> {
    if (!fileOrBlob) throw new Error('fileOrBlob required');
    const cleanFolder = (folder || 'uploads').replace(/^\/+|\/+$/g, '');
    const ext = (fileOrBlob as File).name ? (((fileOrBlob as File).name.split('.').pop() || 'jpg').toLowerCase()) : 'jpg';
    const name = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}.${ext}`;
    const path = `${cleanFolder}/${name}`;
    const { error } = await supabase.storage.from('images').upload(path, fileOrBlob, { contentType, upsert: false });
    if (error) throw error;
    const { data } = supabase.storage.from('images').getPublicUrl(path);
    return data.publicUrl as string;
}
